import { useState } from 'react';
import { ouvreDocument } from '../../api/documents';
import { Btn } from '../../components/ui/Btn.jsx';
import { FRAIS_CAT } from '../../constants/accounting';
import { DEVISE, fmt } from '../../lib/currency';
import { dateSuisse } from '../../lib/dates';
import { localeDates, tr, trf } from '../../lib/i18n';
import { libelleMoyen } from '../../lib/paye-le';
import { tvaRecuperable } from '../../lib/vat';
import { C } from '../../theme/palette';

/** « 10.10.2026 » ou « 2026-10-10 » → « 2026-10-10 », pour trier et filtrer. */
export function dateIso(date) {
  const s = String(date || '');
  const suisse = /^(\d{1,2})\.(\d{1,2})\.(\d{4})$/.exec(s);
  if (suisse)
    return suisse[3] + '-' + suisse[2].padStart(2, '0') + '-' + suisse[1].padStart(2, '0');
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : '';
}

/** « 2026-10 » écrit « octobre 2026 », dans la langue de l'écran. */
function moisLisible(mois) {
  const m = /^(\d{4})-(\d{2})$/.exec(mois || '');
  if (!m) return mois || '';
  return new Date(+m[1], +m[2] - 1, 1).toLocaleDateString(localeDates(), {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Une date (« 2026-10-10 ») est-elle dans la période choisie ?
 *
 * <p>« Comment choisir de quelle date à quelle date j'imprime, par semestre ou
 * par mois, pour éviter de tout imprimer ? » Les périodes : `tout`, une année
 * (`2026`), un mois (`2026-10`), un trimestre (`2026-T3`), un semestre
 * (`2026-S1`), ou `libre`, du … au … (dates comprises).
 *
 * @param {string} iso date de la pièce, AAAA-MM-JJ
 * @param {string} periode la période
 * @param {string} [du] début de la période libre, AAAA-MM-JJ
 * @param {string} [au] fin de la période libre, AAAA-MM-JJ
 * @returns {boolean} vrai si la pièce en fait partie
 */
export function dansLaPeriode(iso, periode, du, au, mois) {
  if (!periode || periode === 'tout') return true;
  if (!iso) return false;
  if (periode === 'libre') return (!du || iso >= du) && (!au || iso <= au);
  // « Plusieurs mois… » : les mois cochés, l'un après l'autre ou non.
  if (periode === 'plusieurs') return (mois || []).includes(iso.slice(0, 7));
  const tri = /^(\d{4})-T([1-4])$/.exec(periode);
  const sem = /^(\d{4})-S([12])$/.exec(periode);
  if (tri || sem) {
    const [annee, n] = tri ? [tri[1], +tri[2]] : [sem[1], +sem[2]];
    const longueur = tri ? 3 : 6;
    const mois = +iso.slice(5, 7);
    return iso.startsWith(annee) && mois > (n - 1) * longueur && mois <= n * longueur;
  }
  return iso.startsWith(periode);
}

/** La période écrite en clair, en tête des feuilles. */
export function libellePeriode(periode, du, au, mois) {
  if (!periode || periode === 'tout') return '';
  if (periode === 'plusieurs') return [...(mois || [])].sort().map(moisLisible).join(', ');
  if (periode === 'libre') {
    if (du && au) return trf('du {0} au {1}', dateSuisse(du), dateSuisse(au));
    if (du) return trf('dès le {0}', dateSuisse(du));
    if (au) return trf('jusqu’au {0}', dateSuisse(au));
    return '';
  }
  const tri = /^(\d{4})-T([1-4])$/.exec(periode);
  if (tri) return trf('Trimestre {0} · {1}', tri[2], tri[1]);
  const sem = /^(\d{4})-S([12])$/.exec(periode);
  if (sem) return trf('Semestre {0} · {1}', sem[2], sem[1]);
  return periode.length === 4 ? periode : moisLisible(periode);
}

/**
 * Le choix « Café + Repas » : les petits tickets du quotidien (le café du
 * matin, le resto de midi) ensemble sur les mêmes feuilles, par date.
 */
export const CAFE_ET_REPAS = 'cafe+repas';
const TYPES_CAFE_ET_REPAS = ['cafe', 'repas'];

/** « ☕ Café + 🍽 Repas / repr. », avec les mots déjà traduits. */
function libelleCafeEtRepas(icones) {
  return TYPES_CAFE_ET_REPAS.map(
    (k) => (icones ? FRAIS_CAT[k].icon + ' ' : '') + tr(FRAIS_CAT[k].lib),
  ).join(' + ');
}

/**
 * Les groupes à poser sur des feuilles A4 : une catégorie par groupe, dans
 * l'ordre du plan des catégories, chaque groupe trié par date, avec son total
 * et sa TVA récupérable — calculés comme sur la page des dépenses.
 *
 * @param {object[]} frais dépenses
 * @param {{categorie: string, periode: string}} choix catégorie (`toutes`,
 *   une clé de FRAIS_CAT ou CAFE_ET_REPAS, un seul groupe pour les deux) et
 *   période (`tout`, `AAAA` ou `AAAA-MM`)
 * @param {object} [soc] entreprise, pour les taux de TVA
 * @returns {{titre: string, ids: number[], total: string, tva: string}[]} les groupes
 */
export function groupesDesTickets(frais, { categorie, periode, du, au }, soc) {
  const retenues = (frais || []).filter((x) => {
    if (typeof x.id !== 'number') return false;
    if (categorie === CAFE_ET_REPAS) {
      if (!TYPES_CAFE_ET_REPAS.includes(x.type)) return false;
    } else if (categorie !== 'toutes' && x.type !== categorie) return false;
    return dansLaPeriode(dateIso(x.date), periode, du, au);
  });
  if (categorie === CAFE_ET_REPAS) {
    if (!retenues.length) return [];
    const lignes = [...retenues].sort(
      (a, b) => dateIso(a.date).localeCompare(dateIso(b.date)) || a.id - b.id,
    );
    const total = lignes.reduce((s, x) => s + (+x.montant || 0), 0);
    return [
      {
        titre: libelleCafeEtRepas(false),
        ids: lignes.map((x) => x.id),
        total: DEVISE + ' ' + fmt(total),
        tva: DEVISE + ' ' + fmt(tvaRecuperable(lignes, FRAIS_CAT, soc)),
        tickets: true,
      },
    ];
  }
  const ordre = Object.keys(FRAIS_CAT);
  const types = [...new Set(retenues.map((x) => x.type))].sort(
    (a, b) => (ordre.indexOf(a) + 1 || 999) - (ordre.indexOf(b) + 1 || 999),
  );
  return types.map((type) => {
    const lignes = retenues
      .filter((x) => x.type === type)
      .sort((a, b) => dateIso(a.date).localeCompare(dateIso(b.date)) || a.id - b.id);
    const total = lignes.reduce((s, x) => s + (+x.montant || 0), 0);
    return {
      titre: tr((FRAIS_CAT[type] || {}).lib || type),
      ids: lignes.map((x) => x.id),
      total: DEVISE + ' ' + fmt(total),
      tva: DEVISE + ' ' + fmt(tvaRecuperable(lignes, FRAIS_CAT, soc)),
      // Café, restaurant : des tickets de caisse, plusieurs par feuille, même
      // photographiés au téléphone (forme d'une page).
      tickets: TYPES_CAFE_ET_REPAS.includes(type),
    };
  });
}

/**
 * La demande des feuilles A4 de tickets : les mêmes feuilles depuis Dépenses
 * et depuis le classeur de la Compta.
 *
 * @param {object} p
 * @param {object[]} p.frais dépenses
 * @param {string} p.categorie `toutes`, une clé de FRAIS_CAT ou CAFE_ET_REPAS
 * @param {string} p.periode `tout`, `AAAA`, `AAAA-MM`, `AAAA-T1`…`T4`, `AAAA-S1`/`S2` ou `libre`
 * @param {string} [p.du] début de la période libre, AAAA-MM-JJ
 * @param {string} [p.au] fin de la période libre, AAAA-MM-JJ
 * @param {object} [p.soc] entreprise
 * @returns {object} le corps de la demande, pour `/depenses/tickets-a4`
 */
export function demandeTicketsA4({ frais, categorie, periode, du, au, soc }) {
  return {
    societe: soc?.nom || '',
    periode: libellePeriode(periode, du, au),
    devise: DEVISE,
    libelles: {
      pieces: tr('pièces'),
      page: tr('Page'),
      total: tr('Total'),
      tva: tr('dont TVA récupérable'),
      sansJustificatif: tr('Sans justificatif'),
      especes: libelleMoyen('especes'),
      carte: libelleMoyen('carte'),
      banque: libelleMoyen('banque'),
    },
    groupes: groupesDesTickets(frais, { categorie, periode, du, au }, soc),
  };
}

/**
 * Les mois qui ont des dépenses (« AAAA-MM »), le plus récent d'abord.
 *
 * @param {object[]} depenses dépenses
 * @returns {string[]} les mois
 */
export function moisDesDepenses(depenses) {
  return [...new Set((depenses || []).map((x) => dateIso(x.date).slice(0, 7)).filter(Boolean))]
    .sort()
    .reverse();
}

/**
 * Les choix d'une liste « Période » : toutes les dates, du … au …, puis les
 * années, semestres, trimestres et mois qui ont des pièces. Les mêmes pour
 * les tickets sur A4 et pour l'envoi des dépenses.
 *
 * @param {object} props propriétés
 * @param {string[]} props.mois mois qui ont des pièces (« AAAA-MM »)
 * @returns {import('react').ReactElement} les options
 */
export function OptionsPeriode({ mois, plusieurs = false }) {
  const annees = [...new Set(mois.map((m) => m.slice(0, 4)))];
  // Les trimestres et semestres qui ont des pièces, année par année.
  const morceaux = (a, n, longueur) =>
    mois.some(
      (m) => m.startsWith(a) && +m.slice(5) > (n - 1) * longueur && +m.slice(5) <= n * longueur,
    );
  return (
    <>
      <option value="tout">{tr('Toutes les dates')}</option>
      <option value="libre">{tr('Du … au … (dates à choisir)')}</option>
      {plusieurs && <option value="plusieurs">{tr('Plusieurs mois…')}</option>}
      {annees.map((a) => (
        <option key={a} value={a}>
          {tr('Année')} {a}
        </option>
      ))}
      {annees.flatMap((a) =>
        [1, 2]
          .filter((n) => morceaux(a, n, 6))
          .map((n) => (
            <option key={a + '-S' + n} value={a + '-S' + n}>
              {libellePeriode(a + '-S' + n)}
            </option>
          )),
      )}
      {annees.flatMap((a) =>
        [1, 2, 3, 4]
          .filter((n) => morceaux(a, n, 3))
          .map((n) => (
            <option key={a + '-T' + n} value={a + '-T' + n}>
              {libellePeriode(a + '-T' + n)}
            </option>
          )),
      )}
      {mois.map((m) => (
        <option key={m} value={m}>
          {moisLisible(m)}
        </option>
      ))}
    </>
  );
}

/**
 * « 🖨 Tickets sur A4 » : les justificatifs des dépenses, plusieurs par
 * feuille, une catégorie à la fois. Le patron classait ses tickets de café un
 * par feuille.
 *
 * @param {object} props propriétés du composant
 * @param {object[]} props.frais dépenses
 * @param {object} [props.soc] entreprise
 * @param {Function} props.onClose ferme le panneau
 * @param {string} [props.categorieInitiale] catégorie choisie à l'ouverture
 * @param {string} [props.periodeInitiale] période choisie à l'ouverture
 * @returns {import('react').ReactElement} le panneau de choix
 */
export function TicketsA4({
  frais,
  soc,
  onClose,
  categorieInitiale = 'toutes',
  periodeInitiale = 'tout',
}) {
  const [categorie, setCategorie] = useState(categorieInitiale);
  const [periode, setPeriode] = useState(periodeInitiale);
  const [du, setDu] = useState('');
  const [au, setAu] = useState('');
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const enregistrees = (frais || []).filter((x) => typeof x.id === 'number');
  const categories = Object.keys(FRAIS_CAT).filter((k) => enregistrees.some((x) => x.type === k));
  const mois = moisDesDepenses(enregistrees);
  const groupes = groupesDesTickets(frais, { categorie, periode, du, au }, soc);
  const nombre = groupes.reduce((s, g) => s + g.ids.length, 0);

  const imprime = async () => {
    setErreur('');
    setEnCours(true);
    try {
      await ouvreDocument(
        '/depenses/tickets-a4',
        undefined,
        demandeTicketsA4({ frais, categorie, periode, du, au, soc }),
      );
    } catch (e) {
      if (!e?.dejaAffiche) setErreur(e?.message || tr('Les feuilles n’ont pas pu être créées.'));
    } finally {
      setEnCours(false);
    }
  };

  const champ = {
    width: '100%',
    border: '1px solid ' + C.border,
    borderRadius: 9,
    padding: '11px',
    fontSize: 13,
    marginBottom: 10,
    boxSizing: 'border-box',
    background: '#fff',
  };
  const etiquette = {
    fontSize: 9,
    fontWeight: 700,
    color: C.muted,
    margin: '2px 0 6px',
    textTransform: 'uppercase',
  };

  return (
    <div
      style={{
        background: '#fff',
        border: '2px solid ' + C.navy,
        borderRadius: 12,
        padding: 13,
        marginBottom: 12,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, color: C.navy, marginBottom: 4 }}>
        🖨 {tr('Tickets sur A4')}
      </div>
      <div style={{ fontSize: 10.5, color: C.grey, marginBottom: 12, lineHeight: 1.5 }}>
        {tr(
          'Les tickets se rangent à plusieurs par feuille, selon leur taille. Chaque catégorie commence sur une nouvelle feuille, avec son total et la TVA récupérable.',
        )}
      </div>
      <div style={etiquette}>{tr('Catégorie')}</div>
      <select
        aria-label={tr('Catégorie')}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value)}
        style={champ}
      >
        <option value="toutes">{tr('Toutes les catégories')}</option>
        {TYPES_CAFE_ET_REPAS.every((k) => categories.includes(k)) && (
          <option value={CAFE_ET_REPAS}>{libelleCafeEtRepas(true)}</option>
        )}
        {categories.map((k) => (
          <option key={k} value={k}>
            {FRAIS_CAT[k].icon} {tr(FRAIS_CAT[k].lib)}
          </option>
        ))}
      </select>
      <div style={etiquette}>{tr('Période')}</div>
      <select
        aria-label={tr('Période')}
        value={periode}
        onChange={(e) => setPeriode(e.target.value)}
        style={champ}
      >
        <OptionsPeriode mois={mois} />
      </select>
      {periode === 'libre' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <label style={{ flex: 1 }}>
            <span style={{ ...etiquette, display: 'block' }}>{tr('Du')}</span>
            <input
              type="date"
              value={du}
              max={au || undefined}
              onChange={(e) => setDu(e.target.value)}
              style={champ}
            />
          </label>
          <label style={{ flex: 1 }}>
            <span style={{ ...etiquette, display: 'block' }}>{tr('Au')}</span>
            <input
              type="date"
              value={au}
              min={du || undefined}
              onChange={(e) => setAu(e.target.value)}
              style={champ}
            />
          </label>
        </div>
      )}
      <div style={{ fontSize: 11, color: C.ink, marginBottom: 10 }}>
        {nombre} {tr('pièces')}
      </div>
      {erreur && (
        <div role="alert" style={{ fontSize: 10, color: C.red, marginBottom: 10 }}>
          {erreur}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <Btn
            label={enCours ? tr('Préparation du PDF…') : '🖨 ' + tr('Créer les feuilles A4')}
            color={C.navy}
            full
            disabled={!nombre || enCours}
            onClick={imprime}
          />
        </div>
        <Btn label={tr('Fermer')} ghost color={C.grey} onClick={onClose} />
      </div>
    </div>
  );
}

/**
 * Le bouton « Tickets sur A4 » d'un type de dépense, dans le classeur de la
 * Comptabilité : ils ouvrent le même choix que dans Dépenses, la catégorie
 * déjà choisie et l'année affichée, pour pouvoir prendre un mois, un
 * trimestre, un semestre ou « du … au … » avant de créer les feuilles.
 * « Je sais pas où tu as mis, mais ça ne fonctionne pas » : ces boutons
 * créaient tout de suite les feuilles de toute l'année.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.type type de dépense (« repas », « cafe »…)
 * @param {object[]} props.frais toutes les dépenses
 * @param {number|string} props.annee année affichée dans la Comptabilité
 * @param {object} [props.soc] entreprise
 * @returns {import('react').ReactElement} les boutons et, ouvert, le choix
 */
export function BoutonsTicketsA4({ type, frais, annee, soc }) {
  const [ouvert, setOuvert] = useState(null);
  const bascule = (categorie) => setOuvert((o) => (o === categorie ? null : categorie));
  return (
    <div style={{ margin: '4px 0 8px' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: ouvert ? 8 : 0 }}>
        {/* Un seul bouton : « ☕ + 🍽 Café et repas » se choisit dans la
            liste Catégorie de la fenêtre. Le deuxième bouton faisait la même
            chose (« c'est un doublon »). */}
        <Btn label={'🖨 ' + tr('Tickets sur A4')} color={C.teal} sm onClick={() => bascule(type)} />
      </div>
      {ouvert && (
        <TicketsA4
          key={ouvert}
          frais={frais}
          soc={soc}
          categorieInitiale={ouvert}
          periodeInitiale={String(annee)}
          onClose={() => setOuvert(null)}
        />
      )}
    </div>
  );
}
